<template>
  <div class="pmw-pagination">
    <!-- ITENS POR PÁGINA -->
    <div
      v-if="itemsPerPageOptions.length > 1"
      class="pmw-pagination--block pmw-pagination--per-page"
    >
      <Paragraph fontSize="small">Itens por página:</Paragraph>
      <Select
        v-model="perPage"
        :options="itemsPerPageOptions"
        class="pmw-pagination--select"
        @change="actionHandler"
      />
    </div>

    <!-- INFORMAÇÕES DA PAGINAÇÃO -->
    <div class="pmw-pagination--block pmw-pagination--infos">
      <Paragraph fontSize="small"
        >{{ startPageItem }}-{{ lastPageItem }} de
        {{ totalItems }} itens</Paragraph
      >
      <Paragraph fontSize="small">{{ totalPages }} páginas</Paragraph>
    </div>

    <!-- AÇÕES DA PAGINAÇÃO -->
    <div class="pmw-pagination--block pmw-pagination--actions">
      <!-- VOLTAR -->
      <button
        :class="{
          disabled: pageSelected.value === 1
        }"
        class="pmw-pagination--nav pmw-pagination--nav__prev"
        @click="prevPage"
      >
        <Icon icon="arrow-line" />
      </button>

      <!-- PÁGINA ATUAL -->
      <Select
        v-model="pageSelected"
        :options="selectPageOptions"
        :disabled="selectPageOptions.length < 2"
        class="pmw-pagination--select"
        @change="setPage"
      />

      <!-- AVANÇAR -->
      <button
        :class="{
          disabled: pageSelected.value >= totalPages
        }"
        class="pmw-pagination--nav pmw-pagination--nav__next"
        @click="nextPage"
      >
        <Icon icon="arrow-line" />
      </button>
    </div>
  </div>
</template>

<script>
import Paragraph from '@/components/_base/elements/Paragraph'
import Select from '@/components/_base/elements/Select'
import Icon from '@/components/_base/elements/Icon'

/** Componente padrão de paginação usado em listagens */
export default {
  name: 'PmwPagination',
  components: {
    Select,
    Paragraph,
    Icon
  },
  props: {
    /** Total de itens máximo listados por página */
    itemsPerPage: {
      type: [Number, String],
      default: 25,
      validator(value) {
        return !isNaN(+value) && value > -1
      }
    },

    /** Opções de visualização de itens por página */
    itemsPerPageOptions: {
      type: Array,
      default: () => [
        {
          text: '25',
          value: 25
        }
      ]
    },

    /**
     * Itens para serem paginados
     * Opcional - usado para quando os itens da paginação
     * forem tratados pelo componente
     */
    items: {
      type: Array,
      default: () => []
    },

    /** Página corrente */
    currentPage: {
      type: [Number, String],
      default: 1,
      validator(value) {
        return !isNaN(+value) && value > -1
      }
    },

    /**
     * Número total de itens
     * * Usado para quando a paginação é feita fora do componente
     */
    totalItems: {
      type: [Number, String],
      default: 0,
      validator(value) {
        return !isNaN(+value) && value > -1
      }
    }
  },
  data() {
    return {
      perPage: {
        text: '25',
        value: 25
      } /** Quantidade de itens selecionados para a exibição */,
      pageSelected: {
        text: '1',
        value: 1
      } /** Página selecionada para visualização */
    }
  },
  computed: {
    /** Retorna a quantidade total de páginas */
    totalPages() {
      if (this.totalItems > 0)
        return Math.ceil(this.totalItems / this.perPage.value)

      return 0
    },
    /** Retorna as opções de páginas para navegação direta */
    selectPageOptions() {
      const options = []

      for (let i = 1; i <= this.totalPages; i++) {
        const optionObj = {
          text: `${i}`,
          value: i
        }

        options.push(optionObj)
      }

      return options
    },

    /** Posição do último item visualizado da página */
    lastPageItem() {
      let lastPageItem = this.perPage.value * this.pageSelected.value

      /** Se o índice ultrapasso o total */
      if (lastPageItem > this.totalItems) {
        /** Recalcula considerando a diferença */
        lastPageItem = lastPageItem - (lastPageItem - this.totalItems)
      }

      return lastPageItem
    },
    /** Indíce do primeiro item da página atual */
    startPageItem() {
      if (this.pageSelected.value > 1)
        return (
          this.pageSelected.value * this.perPage.value -
          (this.perPage.value - 1)
        )

      return 1
    }
  },
  watch: {
    /** Se hover modificação no currentPage */
    currentPage(newVal) {
      if (newVal !== this.pageSelected) {
        this.pageSelected = {
          text: `${newVal}`,
          value: newVal
        }
      }
    }
  },
  created() {
    try {
      this.perPage = this.itemsPerPageOptions.filter(
        (item) => item.value === +this.itemsPerPage
      )[0]
    } catch (error) {
      throw new Error('A quantidade de itens por página não é uma opção válida')
    }
  },
  methods: {
    /** Volta uma página */
    prevPage() {
      if (this.pageSelected.value > 1) {
        this.pageSelected = {
          text: `${this.pageSelected.value - 1}`,
          value: this.pageSelected.value - 1
        }

        this.actionHandler()
      }
    },
    /** Avança uma página */
    nextPage() {
      if (this.pageSelected.value < this.totalPages) {
        this.pageSelected = {
          text: `${this.pageSelected.value + 1}`,
          value: this.pageSelected.value + 1
        }

        this.actionHandler()
      }
    },
    /** Acessa uma página diretamente */
    setPage(page) {
      this.pageSelected = page
      this.actionHandler()
    },
    /** Dispara um evento a cada ação executada na paginação */
    actionHandler() {
      this.$emit('changePagination', {
        currentPage: this.pageSelected.value,
        perPage: this.perPage.value,
        startItemIndex: this.startPageItem,
        lastItemIndex: this.lastPageItem
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.pmw-pagination {
  display: flex;
  border: 1px solid $gray-border;
  background-color: $white;

  &--block {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 1;
    padding: 0 20px;
    height: 42px;
  }

  &--per-page {
    flex: 1 1 150px;
    white-space: nowrap;
    border-right: 1px solid $gray-border;

    .select {
      margin-left: 6px;
    }
  }

  &--infos {
    flex: 1 1 100%;
  }

  &--select {
    width: 40px;

    ::v-deep .select--button {
      border: none;

      &__caret {
        &:after {
          border-top-color: $blue;
        }
      }

      .label__selected {
        color: $blue;
        font-weight: font-weight('weight-medium');
      }
    }
  }

  &--actions {
    padding: 0;

    .select {
      width: auto;
      min-width: 75px;
      padding: 0 15px;
    }
  }

  &--nav {
    @include reset-button;
    height: 100%;
    width: 48px;
    color: $gray-dark;
    opacity: 1;
    transition: opacity $color-anim linear;

    .icon {
      width: 16px;
      height: 16px;
    }

    border: {
      left: 1px solid $gray-border;
    }

    &__prev {
      position: relative;
      transform: scaleX(-1);
      border: {
        left: 1px solid $gray-border;
        right: 1px solid $gray-border;
      }
    }

    &.disabled {
      cursor: default;
      pointer-events: none;
      user-select: none;
      opacity: 0.5;
    }
  }
}
</style>
